import { partition } from '@spendesk/general-type-helpers/Array';
import { isNotNullable } from '@spendesk/general-type-helpers/misc';
import { useMutation, useQueryClient } from 'react-query';

import {
  deleteV2ApiOrganisationIdSettingsSettingsId,
  postV2ApiOrganisationIdSettingsBulkDelete,
} from 'src/client/spendesk-api';
import { useCompanyId } from 'src/core/modules/app/hooks/useCompanyId';
import { useOrganisationId } from 'src/core/modules/app/hooks/useOrganisationId';
import { apiUrl } from 'src/core/utils/api';

import { type Setting } from '../setting';

const deleteSingleEntityCostCenter = async (
  companyId: string,
  costCenterId: string,
) => {
  const response = await fetch(
    apiUrl(`/cost-centers/${costCenterId}`, companyId),
    {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'Content-Type': 'application/json',
      },
    },
  );

  if (!response.ok) {
    throw new Error('Failed to delete cost center');
  }
};

const deleteMultiEntityCostCenter = async (
  organisationId: string,
  settingsId: string,
) => {
  const response = await deleteV2ApiOrganisationIdSettingsSettingsId({
    path: {
      organisationId,
      settingsId,
    },
  });

  if (response.statusCode !== 200) {
    throw new Error('Failed to delete cost center');
  }
};

const deleteBulkCostCenters = async (
  organisationId: string,
  organisationSettings: Setting[],
) => {
  const [costCenters, settings] = partition(
    organisationSettings,
    (setting) => setting.type === 'entity-specific',
  );
  const response = await postV2ApiOrganisationIdSettingsBulkDelete({
    path: {
      organisationId,
    },
    body: {
      settingsIds: settings.map((setting) => setting.id),
      costCenterIds: costCenters
        .map((costCenter) => costCenter.entities?.[0]?.costCenterId)
        .filter(isNotNullable),
    },
  });

  if (response.statusCode !== 200) {
    throw new Error('Failed to bulk delete cost centers');
  }
};

export const useDeleteOrganisationCostCenter = () => {
  const organisationId = useOrganisationId();
  const companyId = useCompanyId();
  const queryClient = useQueryClient();

  return useMutation(async (settings: Setting[]) => {
    if (settings.length === 1) {
      const setting = settings[0];
      if (
        setting.type === 'entity-specific' &&
        setting.entities?.[0]?.costCenterId
      ) {
        await deleteSingleEntityCostCenter(
          companyId,
          // for entity-specific case there's always one entity and one cost center
          setting.entities?.[0]?.costCenterId,
        );
      } else {
        await deleteMultiEntityCostCenter(organisationId, setting.id);
      }
    } else {
      await deleteBulkCostCenters(organisationId, settings);
    }

    queryClient.invalidateQueries(['settings', organisationId]);
  });
};
